/**
 * Service for managing users from Supabase
 * This will replace/complement the SQL Server user service
 */

import { supabase } from './client';

export interface SupabaseUser {
  id: string;
  legacy_id: string | null;
  email: string;
  comp_id: string | null;
  user_name: string | null;
  phone_number: string | null;
  is_admin: boolean;
  is_bod: boolean;
  is_sales: boolean;
  u_group: string | null;
  avatar: string | null;
  auth_user_id: string | null;
  /** auth.users.id — đồng bộ với auth_user_id (migration legacy) */
  user_id: string | null;
  created_at: string;
  updated_at: string;
  synced_at: string | null;
  sync_source: string | null;
  skills: string | null;
}

export interface UserFilters {
  email?: string;
  comp_id?: string;
  user_name?: string;
  is_admin?: boolean;
  u_group?: string;
}

export class SupabaseUserService {
  /**
   * Get all users with optional filters
   */
  static async getUsers(filters?: UserFilters): Promise<SupabaseUser[]> {
    let query = supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters?.email) {
      query = query.ilike('email', `%${filters.email}%`);
    }
    if (filters?.comp_id) {
      query = query.eq('comp_id', filters.comp_id);
    }
    if (filters?.user_name) {
      query = query.ilike('user_name', `%${filters.user_name}%`);
    }
    if (filters?.is_admin !== undefined) {
      query = query.eq('is_admin', filters.is_admin);
    }
    if (filters?.u_group) {
      query = query.ilike('u_group', `%${filters.u_group}%`);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Error fetching users: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get user by ID
   */
  static async getUserById(id: string): Promise<SupabaseUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // User not found
      }
      throw new Error(`Error fetching user: ${error.message}`);
    }

    return data;
  }

  /**
   * Get user by legacy ID (from SQL Server)
   */
  static async getUserByLegacyId(legacyId: string): Promise<SupabaseUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('legacy_id', legacyId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // User not found
      }
      throw new Error(`Error fetching user: ${error.message}`);
    }

    return data;
  }

  /**
   * Get user by email
   */
  static async getUserByEmail(email: string): Promise<SupabaseUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // User not found
      }
      throw new Error(`Error fetching user: ${error.message}`);
    }

    return data;
  }

  /**
   * Get user by auth_user_id
   */
  static async getUserByAuthId(authUserId: string): Promise<SupabaseUser | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .or(`auth_user_id.eq.${authUserId},user_id.eq.${authUserId}`)
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Error fetching user: ${error.message}`);
    }

    return data;
  }

  /**
   * Update user profile
   */
  static async updateUser(id: string, updates: Partial<SupabaseUser>): Promise<SupabaseUser> {
    const { data, error } = await supabase
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Error updating user: ${error.message}`);
    }

    return data;
  }

  /**
   * Link user with auth.users
   */
  static async linkAuthUser(email: string, authUserId: string): Promise<SupabaseUser> {
    const { data, error } = await supabase.functions.invoke('link-auth-user', {
      body: {
        email,
        auth_user_id: authUserId
      }
    });

    if (error) {
      throw new Error(`Error linking user: ${error.message}`);
    }

    return data.user;
  }


  /**
   * Get current authenticated user's profile
   */
  static async getCurrentUserProfile(): Promise<SupabaseUser | null> {
    const { data: authData } = await supabase.auth.getUser();
    
    if (!authData.user) {
      return null;
    }

    return this.getUserByAuthId(authData.user.id);
  }
}